// Config, helper e tipi condivisi della console Invio Pratiche (estratti
// move-only da InvioPratiche.tsx, Fase 5 — vedi docs/superpowers/plans/
// 2026-08-06-refactor-invio-pratiche.md).
import type {
  A2AInviabileRow, A2APrefill,
  SentraInviabileRow, SentraPrefill,
  IrenInviabileRow, IrenPrefill,
} from "@/types/admin";

export const STATO_BADGE: Record<string, string> = {
  da_inviare: "bg-slate-100 text-slate-700 border border-slate-300",
  in_corso: "bg-amber-100 text-amber-800 border border-amber-300",
  bozza: "bg-sky-600 text-white border border-sky-700 font-semibold",
  inviata: "bg-green-100 text-green-800 border border-green-300",
  errore: "bg-red-100 text-red-800 border border-red-300",
  accettata: "bg-emerald-600 text-white border border-emerald-700 font-semibold",
  attivata: "bg-violet-600 text-white border border-violet-700 font-semibold",
  annullata: "bg-rose-100 text-rose-700 border border-rose-300",
  sospesa: "bg-orange-100 text-orange-800 border border-orange-300",
};
export const STATO_LABEL: Record<string, string> = {
  da_inviare: "Da inviare", in_corso: "In corso", bozza: "Bozza pronta",
  inviata: "Inviata", errore: "Errore", accettata: "Accettata", attivata: "Attivata",
  annullata: "Annullata",
  // pratica annullata sul fornitore: l'invio automatico la ignora, resta inviabile a mano
  sospesa: "Sospesa (no auto-invio)",
};
// override Iren: "bozza"/stati intermedi condividono la chiave con A2A/Sentra ma il
// significato è diverso (nessun "prepara bozza" separato lato UI Iren) → dizionario dedicato,
// applicato solo per le righe fornitore==="Iren" (vedi statoLabel/statoBadgeCls).
export const IREN_STATO_LABEL: Record<string, string> = {
  bozza: "Bozza Iren", attesa_firma: "In attesa firma cliente", firmata: "Firmata",
};
export const IREN_STATO_BADGE: Record<string, string> = {
  bozza: "bg-sky-600 text-white border border-sky-700 font-semibold",
  attesa_firma: "bg-amber-100 text-amber-800 border border-amber-300",
  firmata: "bg-green-100 text-green-800 border border-green-300",
};
export function statoLabel(r: UnifiedRow): string {
  if (r.fornitore === "Iren" && IREN_STATO_LABEL[r.stato]) return IREN_STATO_LABEL[r.stato];
  return STATO_LABEL[r.stato] ?? r.stato;
}
export function statoBadgeCls(r: UnifiedRow): string {
  if (r.fornitore === "Iren" && IREN_STATO_BADGE[r.stato]) return IREN_STATO_BADGE[r.stato];
  return STATO_BADGE[r.stato] ?? STATO_BADGE.da_inviare;
}
// stati "conclusi" su A2A: solo dettaglio in lista, niente azioni di invio/annullo dal pannello
export const STATI_CONCLUSI = new Set(["accettata", "attivata", "annullata"]);

export const FORNITORE_BADGE: Record<"A2A" | "Sentra" | "Iren", string> = {
  A2A: "bg-indigo-100 text-indigo-800 border border-indigo-300",
  Sentra: "bg-teal-100 text-teal-800 border border-teal-300",
  Iren: "bg-orange-100 text-orange-800 border border-orange-300",
};

export const TIPI_PRATICA = ["Cambio", "Voltura", "Subentro", "Nuova attivazione"] as const;

export const SELECT_CLS =
  "h-8 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 cursor-pointer";

export function fmtData(s: string): string {
  if (!s) return "—";
  // EspoCRM salva "YYYY-MM-DD HH:MM:SS" in UTC senza suffisso → trattalo come UTC e converti
  // al fuso locale del browser (Europe/Rome), come dates.ts. Senza la 'Z' l'orario usciva 2h
  // indietro (CEST) → pratiche "salvate" all'ora sbagliata.
  const d = new Date(s.includes(" ") ? s.replace(" ", "T") + "Z" : s);
  if (isNaN(d.getTime())) return s;
  const data = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  const ora = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return `${data} ${ora}`;
}

// FINDING 5 (review): documento.scadenza è una data pura "YYYY-MM-DD" (no time/tz) —
// solo display, il valore ISO resta invariato nello stato.
export function fmtDataSolo(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

// --- Riga unificata (A2A + Sentra + Iren): il fornitore è un tag CLIENT-SIDE, il backend non
// lo restituisce — è determinato da quale endpoint ha prodotto la riga (mapA2ARow/mapSentraRow/
// mapIrenRow).
export interface UnifiedRow {
  fornitore: "A2A" | "Sentra" | "Iren";
  opp_id: string;
  cliente: string;
  tipo: string;
  commodity: string;
  offerta: string;
  stato: string;
  stage: string;
  created_at: string;
  // A2A-only
  a2a_opp_id?: string | null;
  a2a_numero_pratica?: string | null;
  utenza_username?: string | null;
  utenza_stato?: string | null;
  moduli_mancanti?: string[];
  motivo_non_inviabile?: string | null;
  motivo_annullamento?: string | null;
  motivo_errore?: string | null;
  // Sentra-only
  contratto_id?: string | null;
  // Iren-only
  iren_pratica_id?: string | null;
}

export function mapA2ARow(r: A2AInviabileRow): UnifiedRow {
  return {
    fornitore: "A2A", opp_id: r.opp_id, cliente: r.cliente, tipo: r.tipo, commodity: r.commodity,
    offerta: r.offerta, stato: r.stato, stage: r.stage, created_at: r.created_at,
    a2a_opp_id: r.a2a_opp_id, a2a_numero_pratica: r.a2a_numero_pratica,
    moduli_mancanti: r.moduli_mancanti, motivo_non_inviabile: r.motivo_non_inviabile,
    motivo_annullamento: r.motivo_annullamento, motivo_errore: r.motivo_errore,
    utenza_username: r.utenza_username, utenza_stato: r.utenza_stato,
  };
}
export function mapSentraRow(r: SentraInviabileRow): UnifiedRow {
  return {
    fornitore: "Sentra", opp_id: r.opp_id, cliente: r.cliente, tipo: r.tipo, commodity: r.commodity,
    offerta: r.offerta, stato: r.stato, stage: r.stage, created_at: r.created_at,
    contratto_id: r.contratto_id,
  };
}
export function mapIrenRow(r: IrenInviabileRow): UnifiedRow {
  return {
    fornitore: "Iren", opp_id: r.opp_id, cliente: r.cliente, tipo: r.tipo, commodity: r.commodity,
    offerta: r.offerta, stato: r.stato, stage: r.stage, created_at: r.created_at,
    iren_pratica_id: r.iren_pratica_id, moduli_mancanti: r.moduli_mancanti, motivo_errore: r.motivo_errore,
  };
}

// prefill tagged col fornitore che l'ha prodotto, per narrowing sui campi esclusivi
// (moduli_mancanti/a2a_opp_id/recapito_bolletta su A2A, contratto_id su Sentra, email_firma/
// offerta_nome su Iren — schema diverso, niente tipo_cliente/metodo_pagamento/iban)
export type PanelPrefill =
  | (A2APrefill & { fornitore: "A2A" })
  | (SentraPrefill & { fornitore: "Sentra" })
  | (IrenPrefill & { fornitore: "Iren" });

export interface KpiView {
  da_inviare: number; in_corso: number; inviata: number;
  accettata: number; attivata: number; annullata: number; errore: number;
}
export const ZERO_KPI: KpiView = { da_inviare: 0, in_corso: 0, inviata: 0, accettata: 0, attivata: 0, annullata: 0, errore: 0 };
